import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Renders `children` only once the placeholder scrolls near the viewport.
 * Keeps heavy below-the-fold chunks (and their JS) out of the initial
 * hydration/parse work — the main win for mobile TBT / time-to-interactive.
 */
export function DeferUntilVisible({
  children,
  minHeight,
  rootMargin = "600px",
}: {
  children: ReactNode;
  /** Reserved height for the placeholder so layout stays stable (CLS). */
  minHeight: number;
  rootMargin?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShow(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    // Safety net: mount after the page settles even without scrolling, so
    // crawlers and idle visitors always get the content.
    const timer = window.setTimeout(() => {
      setShow(true);
      io.disconnect();
    }, 2500);
    return () => {
      window.clearTimeout(timer);
      io.disconnect();
    };
  }, [show, rootMargin]);

  if (show) return <>{children}</>;
  return <div ref={ref} style={{ minHeight }} aria-hidden />;
}
